import {
  ClassSchedule,
  ScheduleData,
  ServiceSchedule,
} from '@/widgets/FindAClass/classfinder.interfaces';

import { SCHEDULE_DATE_FORMAT } from '../classfinder.constants';
import dayjs from '@/common/lib/dayjs';
import { convertUTCToLocal } from './classfinder.utils';

interface OrganiseSchedulesProps {
  schedules: ClassSchedule[] | ServiceSchedule[];
  selectedTime: string | null;
  selectedDate: dayjs.Dayjs | null;
  selectedClassFilters: string[];
  currentEndDate?: dayjs.Dayjs;
}

export default function organiseSchedules({
  schedules,
  selectedTime,
  selectedDate,
  selectedClassFilters,
  currentEndDate,
}: OrganiseSchedulesProps) {
  if (!schedules || schedules.length === 0 || !selectedDate) {
    return { grouped: {}, filtered: {} };
  }

  const referenceDateTime =
    selectedTime && selectedDate
      ? selectedDate
          .hour(parseInt(selectedTime.split(':')[0]))
          .minute(parseInt(selectedTime.split(':')[1]))
      : null;

  const dateRange = new Set<string>();
  const today = dayjs().startOf('day');
  const endDate = currentEndDate || today.add(13, 'day');

  for (let i = 0; i <= endDate.diff(today, 'days'); i++) {
    dateRange.add(today.add(i, 'day').format(SCHEDULE_DATE_FORMAT));
  }

  const grouped = Array.from(dateRange).reduce((acc, day) => {
    acc[day] = {
      schedules: [],
      date: dayjs(day),
    };
    return acc;
  }, {} as ScheduleData);

  schedules.forEach((schedule) => {
    schedule.slots.forEach((slot) => {
      const localStartDateTime = convertUTCToLocal(slot.startDateTime);
      const slotDateTime = dayjs(localStartDateTime);
      if (!slotDateTime.isValid()) return;

      const day = slotDateTime.format(SCHEDULE_DATE_FORMAT);

      if (!dateRange.has(day)) return;

      const isServiceSchedule = 'service' in schedule;

      if (isServiceSchedule) {
        grouped[day].schedules.push({
          id: `${slot.id}-${day}-${schedule.service.id}`,
          start_datetime: localStartDateTime,
          class: {
            name: schedule.service.title,
            duration: `${schedule.service.duration} min`,
            imgUrl: schedule.service.imgUrl,
            description: schedule.service.description,
            address: {
              address_line1: slot?.location?.name,
              address_line2: slot?.location?.description,
            },
          },
        });
      } else {
        grouped[day].schedules.push({
          id: `${slot.id}-${day}-${schedule.class.id}`,
          start_datetime: localStartDateTime,
          class: {
            name: schedule.class.title,
            duration: `${schedule.class.duration} min`,
            imgUrl: schedule.class.imgUrl,
            description: schedule.class.description,
            address: {
              address_line1: slot?.location?.name,
              address_line2: slot?.location?.description,
            },
          },
        });
      }
    });
  });

  const filteredGrouped = Object.entries(grouped).reduce((acc, [day, data]) => {
    if (dateRange.has(day)) {
      acc[day] = data;
    }
    return acc;
  }, {} as ScheduleData);

  Object.keys(filteredGrouped).forEach((day) => {
    filteredGrouped[day].schedules.sort((a, b) =>
      dayjs(a.start_datetime).diff(dayjs(b.start_datetime))
    );
  });

  const filtered = Object.keys(filteredGrouped)
    .sort((a, b) => filteredGrouped[a].date.diff(filteredGrouped[b].date))
    .reduce((acc, day) => {
      let daySchedules = filteredGrouped[day].schedules;

      if (
        selectedTime &&
        selectedDate &&
        day === selectedDate.format(SCHEDULE_DATE_FORMAT)
      ) {
        daySchedules = daySchedules.filter(
          (schedule) =>
            !dayjs(schedule.start_datetime).isBefore(referenceDateTime)
        );
      }

      if (selectedClassFilters.length > 0) {
        daySchedules = daySchedules.filter((schedule) => {
          const className = (schedule.class?.name || '').toLowerCase();
          return selectedClassFilters.some((filter) =>
            className.includes(filter.toLowerCase())
          );
        });
      }

      acc[day] = {
        ...filteredGrouped[day],
        schedules: daySchedules,
      };

      return acc;
    }, {} as ScheduleData);

  return {
    grouped: filteredGrouped,
    filtered,
  };
}
